import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Users, CheckCircle2, ShoppingCart, CreditCard, AlertCircle, Globe, ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import DrillDownSheet from './DataFilesAnalytics/DrillDownSheet';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function StatCard({ icon: Icon, label, value, sub, accent = 'bg-zinc-100 text-zinc-600', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'hover:shadow-sm'}`}
      data-testid={`customer-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-zinc-900">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, span = 'col-span-2', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 ${span} transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : ''}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {interactive && <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />}
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}

const fmtMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function CustomersDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const goCustomers = () => navigate('/data/customers');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/api/customers-dashboard');
        setData(r.data);
      } catch (e) {
        // ignore
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-12"><LoadingSpinner /></div>;
  if (!data) return <div className="py-12 text-center text-sm text-muted-foreground">No data yet.</div>;

  const s = data.summary || {};
  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Customers" value={s.total || 0} sub={`${s.active || 0} active`} accent="bg-indigo-50 text-indigo-600" onClick={goCustomers} />
        <StatCard icon={CheckCircle2} label="Active" value={s.active || 0} sub={`${s.inactive || 0} inactive`} accent="bg-emerald-50 text-emerald-600" onClick={goCustomers} />
        <StatCard icon={ShoppingCart} label="Total Orders" value={s.total_orders || 0} accent="bg-amber-50 text-amber-600" onClick={() => navigate('/sales-orders')} />
        <StatCard icon={CreditCard} label="Avg Credit Limit" value={fmtMoney(s.average_credit)} sub={`${fmtMoney(s.total_credit)} total`} accent="bg-violet-50 text-violet-600" onClick={goCustomers} />
      </div>

      {/* Row 1 — type + country */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ChartCard title="Customers by Type" subtitle="Segment distribution" span="md:col-span-2" onClick={goCustomers}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.by_type} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2} style={{ cursor: 'pointer' }}>
                {(data.by_type || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openCustomerDrill('customer_type', row.name, 'Customers by Type')} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Customers by Country" subtitle="Geographic distribution" span="md:col-span-2" onClick={goCustomers}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_country} layout="vertical" margin={{ top: 8, right: 16, left: 20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={110} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} style={{ cursor: 'pointer' }}>
                {(data.by_country || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[(i + 1) % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openCustomerDrill('country', row.name, 'Customers by Country')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2 — onboarding + payment terms */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ChartCard title="New Customers — Last 12 Months" subtitle="Onboarding velocity" span="md:col-span-2" onClick={goCustomers}>
          {(data.new_per_month || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No customer onboarding data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.new_per_month} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="By Payment Terms" subtitle="DSO risk indicator" span="md:col-span-2" onClick={goCustomers}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_terms} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }}>
                {(data.by_terms || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[(i + 4) % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openCustomerDrill('payment_terms', row.name, 'Customers by Payment Terms')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3 — top revenue (tidy table, decimal-aligned) */}
      <ChartCard title="Top 10 Customers by Revenue" subtitle="Sum of sales order gross totals" span="" onClick={() => navigate('/sales-orders')}>
        {(data.top_revenue || []).length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No revenue recorded yet</div>
        ) : (
          <div className="overflow-y-auto h-full">
            <table className="w-full text-sm">
              <tbody>
                {data.top_revenue.map((row, i) => {
                  const maxValue = Math.max(...data.top_revenue.map(r => r.value || 0));
                  const pct = maxValue > 0 ? Math.round((row.value / maxValue) * 100) : 0;
                  return (
                    <tr key={row.name || i}
                        onClick={() => openOrdersDrill(row.name)}
                        className="border-b border-zinc-100 last:border-0 hover:bg-indigo-50 cursor-pointer transition-colors"
                        data-testid={`customer-revenue-drill-${row.name}`}>
                      <td className="py-2 pr-3 w-6 text-xs text-zinc-400 tabular-nums text-right">{i + 1}.</td>
                      <td className="py-2 pr-3 text-left">
                        <div className="font-medium text-zinc-900 truncate">{row.name}</div>
                        <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right text-xs text-zinc-500 tabular-nums whitespace-nowrap">{row.orders} order{row.orders === 1 ? '' : 's'}</td>
                      <td className="py-2 pl-3 text-right font-mono tabular-nums text-zinc-900 whitespace-nowrap">
                        ${(row.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <DrillDownSheet
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        fetchUrl={drill?.fetchUrl}
        columns={drill?.columns || []}
        dataPath={drill?.dataPath}
        filter={drill?.filter}
      />
    </div>
  );
}
